/* eslint-disable @typescript-eslint/no-unsafe-assignment */

import { ChangeDetectionStrategy, Component, computed, effect, inject, OnInit, Signal } from "@angular/core";
import { MatIconRegistry } from "@angular/material/icon";
import { DomSanitizer } from "@angular/platform-browser";
import { AVAILABLE_LANGUAGES, DEFAULT_LANGUAGE } from "./constants";
import { isDefined, TranslateService } from "@ngx-translate/core";
import { SwUpdate } from "@angular/service-worker";
import { MatDialog } from "@angular/material/dialog";
import { ConfirmationComponent } from "./modals/confirmation/confirmation.component";
import { ConfirmationDialogData, ConfirmationDialogResult } from "./modals/confirmation/confirmation-dialog.interface";
import { filter, first, Observable, tap } from "rxjs";
import { User } from "./store/user/interfaces/user.interface";
import { FromProgram, FromRouter, FromUser, RootState } from "./store";
import { Store } from "@ngrx/store";
import { Program } from "./store/program/program.interface";
import { Params, Router } from "@angular/router";
import { PosthogStore } from "./store/posthog/posthog.store";
import { isInternalUser } from "./utils/is-internal-user";
import { toPageviewPaths } from "./utils/pageview-path";

@Component({
  // eslint-disable-next-line @angular-eslint/prefer-standalone
  standalone: false,
  selector: "app-root",
  templateUrl: "./app.component.html",
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AppComponent implements OnInit {
  private readonly matIconRegistry = inject(MatIconRegistry);
  private readonly domSanitzer = inject(DomSanitizer);
  private readonly translate = inject(TranslateService);
  private readonly swUpdate = inject(SwUpdate);
  private readonly dialog = inject(MatDialog);
  private readonly router = inject(Router);

  private readonly store = inject(Store<RootState>);
  private readonly posthogStore = inject(PosthogStore);

  private readonly params$: Observable<Params> = this.store.select(FromRouter.selectRouteParams);

  private readonly program: Signal<Program> = this.store.selectSignal(FromProgram.selectProgram);
  private readonly userId: Signal<string | undefined> = this.store.selectSignal(FromUser.selectUserId);
  private readonly userInfo: Signal<User | undefined> = this.store.selectSignal(FromUser.selectUser);
  private readonly authorities: Signal<string[] | undefined> = this.store.selectSignal(FromUser.selectAuthorities);

  // Re-identifying the same id does not create a new person; email is only a person property. Identify and group
  // wait for consent, and run again once it is given.
  protected readonly posthogProperties = effect(() => {
    const consented = this.posthogStore.privacyPolicy();
    const userId = this.userId();
    const user = this.userInfo();
    const programId = this.program()?.id;
    const authorities = this.authorities() ?? [];

    if (consented && userId && user) {
      this.posthogStore.identify(userId, { email: user.email, is_internal: isInternalUser(authorities, user.email) });
    }
    if (programId) this.posthogStore.register({ program: programId });
    this.posthogStore.register({ roles: authorities });
  });

  private readonly programGroup = computed(
    () => {
      const program = this.program();
      return program?.id ? { id: program.id, name: program.name } : null;
    },
    { equal: (a, b) => a?.id === b?.id && a?.name === b?.name },
  );

  // A group call with properties sends a $groupidentify every time, so it runs only when consent or the program changes.
  protected readonly posthogProgramGroup = effect(() => {
    const group = this.programGroup();
    if (group && this.posthogStore.privacyPolicy()) this.posthogStore.group("program", group.id, { name: group.name });
  });

  constructor() {
    this.translate.use(
      AVAILABLE_LANGUAGES.includes(navigator.language.split("-")[0]) ? navigator.language.split("-")[0] : DEFAULT_LANGUAGE,
    );

    this.matIconRegistry.addSvgIcon("smart_chat", this.domSanitzer.bypassSecurityTrustResourceUrl("assets/icon/smart-chat.svg"));
    this.matIconRegistry.addSvgIcon("add_action", this.domSanitzer.bypassSecurityTrustResourceUrl("assets/icon/add-action.svg"));
    this.matIconRegistry.addSvgIcon(
      "add_action_between",
      this.domSanitzer.bypassSecurityTrustResourceUrl("assets/icon/add-action-between.svg"),
    );
    this.matIconRegistry.addSvgIcon("action_between", this.domSanitzer.bypassSecurityTrustResourceUrl("assets/icon/action-between.svg"));
    // Material Symbols "draft": the Material Icons font loaded in index.html has no such glyph.
    this.matIconRegistry.addSvgIcon("draft", this.domSanitzer.bypassSecurityTrustResourceUrl("assets/icon/draft.svg"));

    if (this.swUpdate.isEnabled) {
      // Check for update every 30 minutes
      setInterval(() => void this.swUpdate.checkForUpdate(), 30 * 60 * 1000);

      this.swUpdate.versionUpdates
        .pipe(
          filter(evt => evt.type === "VERSION_READY"),
          tap(() => {
            const dialogRef = this.dialog.open<ConfirmationComponent, ConfirmationDialogData, ConfirmationDialogResult>(
              ConfirmationComponent,
              {
                data: {
                  title: this.translate.instant("APP_UPDATE"),
                  confirmText: "buttons.confirm",
                },
              },
            );

            dialogRef
              .afterClosed()
              .pipe(
                filter(isDefined),
                filter(({ confirm }: ConfirmationDialogResult) => confirm),
                tap(() => window.location.reload()),
              )
              .subscribe();
          }),
        )
        .subscribe();
    }
  }

  public ngOnInit(): void {
    this.posthogStore.init();

    this.store
      .select(FromUser.selectUser)
      .pipe(
        first(user => !!user),
        tap(() => this.posthogStore.promptConsentIfUnresolved()),
      )
      .subscribe();

    this.router.events
      .pipe(
        toPageviewPaths(this.params$),
        tap(path => this.posthogStore.capturePageview(path)),
      )
      .subscribe();
  }
}
